import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import {
  Form,
  Modal,
  Select,
  Table,
  Input,
  Button,
  notification,
  InputNumber,
  Card,
  Divider,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { MenuOutlined } from "@ant-design/icons";

import { Fetch, Post, Put } from "../../fetch";
import {
  convertIdToKey,
  convertIdtoSeriesName,
  removeUnpublished,
} from "../../utils";
import { Context } from "../../context";
import {
  validateInputFields,
  validateOptionalDescriptionFields,
} from "../../validators";

const { Option } = Select;
const { TextArea } = Input;

const Row = ({ children, ...props }) => {
  console.log("props - ", props);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props.id,
  });
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      }
    ),
    transition,
    ...(isDragging
      ? {
          position: "relative",
          zIndex: 9999,
        }
      : {}),
  };
  return (
    <div
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="editCard"
    >
      <Card size="small" style={{ width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "row" }}>
          <MenuOutlined
            ref={setActivatorNodeRef}
            style={{
              touchAction: "none",
              cursor: "move",
            }}
            {...listeners}
          />
          <Divider type="vertical" />
          <div>{props.name ? props.name : props.id}</div>
        </div>
      </Card>
    </div>
  );
};

const EditPlaylists = ({ id, showModal, handleReset }) => {
  const { apiUrl, token } = useContext(Context);

  const [playlistId, setPlaylistId] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const [content, setContent] = useState([]);
  const [movies, setMovies] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [series, setSeries] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [artists, setArtists] = useState([]);
  const [playlistType, setPlaylistType] = useState("row_1");
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [form] = Form.useForm();

  useEffect(() => {
    Fetch(`${apiUrl}/movies`, token, (result) => {
      const movies = removeUnpublished(convertIdToKey(result.movies));
      setMovies(movies);
    });
    Fetch(`${apiUrl}/episodes/all`, token, (result) => {
      const episodes = removeUnpublished(convertIdToKey(result.episodes));
      setEpisodes(episodes);
    });
    Fetch(`${apiUrl}/series`, token, (result) => {
      const series = convertIdToKey(result.series);
      Fetch(`${apiUrl}/seasons`, token, (result) => {
        if (result.success) {
          const seasons = removeUnpublished(
            convertIdtoSeriesName(series, result.seasons)
          );
          setSeasons(seasons);
        }
      });
      setSeries(series);
    });
    Fetch(`${apiUrl}/crew`, token, (result) => {
      const a = convertIdToKey(result.creators);
      setArtists(a);
    });
  }, [apiUrl, token]);

  useEffect(() => {
    if (!id) {
      return;
    }
    Fetch(`${apiUrl}/playlists/${"home"}/${id}`, token, (result) => {
      if (result.success === true) {
        setPlaylistId(id);
        setContent(result.playlist.content);
        setPlaylistType(result.playlist.playlistType);
        form.setFieldsValue(result.playlist);
      } else {
        notification.error({
          message: "Something Went Wrong",
          description: "Please contact your superadmin/refresh and try again",
        });
      }
    });
  }, [apiUrl, token, id]);

  const handleSubmit = async () => {
    try {
      const fields = await form.validateFields();
      console.log(fields);
      if (
        !["recommended", "languages", "artists", "top10"].includes(
          fields.playlistType
        )
      ) {
        const duplicates = fields.content.filter(
          (item, index) => fields.content.indexOf(item) !== index
        );
        if (duplicates.length > 0) {
          notification.error({
            message: "Manage Playlists Error",
            description: "Content has duplicates",
          });
          return;
        }
      }

      const fn = id ? Put : Post;
      fn(
        id ? `${apiUrl}/playlists/${playlistId}` : `${apiUrl}/playlists`,
        token,
        { ...fields, page: "home", content: content },
        (result) => {
          if (result.success === true) {
            notification.success({
              message: "Success",
              description: "Playlists updated",
            });
            showModal[1]((prev) => !prev);
            handleReset();
          } else if (result.success === false) {
            notification.error({
              message: "Error",
              description: result["message"],
            });
          }
        },
        () => {
          notification.error({
            message: "Manage Playlists Error",
            description: "Check console for more details",
          });
        }
      );
    } catch (err) {
    } finally {
    }
  };

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setContent((previous) => {
        const activeIndex = previous.findIndex((i) => i === active.id);
        const overIndex = previous.findIndex((i) => i === over?.id);
        return arrayMove(previous, activeIndex, overIndex);
      });
    }
  };

  const getContentName = (id) => {
    let content_type, content_id;
    if (id.includes("|")) {
      content_type = id.split("|")[0];
      content_id = id.split("|")[1];
    } else {
      content_type = "creators";
      content_id = id;
    }
    if (content_type === "movie") {
      for (const i in movies) {
        if (movies[i].key === content_id) return movies[i].title;
      }
    } else if (content_type === "season") {
      for (const i in seasons) {
        if (seasons[i]._id === content_id) {
          return `${seasons[i].series_name} - Season ${seasons[i].season}`;
        }
      }
    } else if (content_type === "episode") {
      for (const i in episodes) {
        if (episodes[i].key === content_id) {
          return episodes[i].title;
        }
      }
    } else {
      for (const i in artists) {
        if (artists[i].key === content_id) {
          return artists[i].name;
        }
      }
    }
  };

  return (
    <div>
      {playlistType && (
        <Card>
          <Form layout="vertical" form={form} onFinish={handleSubmit}>
            <Form.Item
              name={"playlistType"}
              label={"Playlist Type"}
              initialValue={"row_1"}
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Please select the Row"
                onChange={(v) => {
                  setPlaylistType(v);
                  setContent([]);
                  form.setFieldValue("content", []);
                }}
                value={playlistType}
                defaultValue={"row_1"}
              >
                <Option value="row_1">Episodes/Standalone</Option>
                <Option value="row_2">Expand on Hover</Option>
                <Option value="row_3">Expand Right on Hover</Option>
                <Option value="row_4">Expand Right on Hover (Wide)</Option>
                <Option value="creators">Creators</Option>
              </Select>
            </Form.Item>
            <Form.Item
              name={"position"}
              label="Position"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <InputNumber min={1} placeholder="Enter Position" />
            </Form.Item>
            <Form.Item
              name="name"
              label="Name"
              rules={[
                {
                  required: true,
                  validator: validateInputFields,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name={"description"}
              label="Description"
              rules={[
                {
                  required: false,
                  validator: validateOptionalDescriptionFields,
                },
              ]}
            >
              <TextArea showCount maxLength={1000} rows={10} />
            </Form.Item>
            <Form.Item
              name={"content"}
              label="Select Content"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                showSearch
                mode="multiple"
                className="form-card-selector"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                filterSort={(optionA, optionB) =>
                  optionA.children
                    .toLowerCase()
                    .localeCompare(optionB.children.toLowerCase())
                }
                style={{ width: "100%" }}
                onChange={(k, v) => setContent(k)}
              >
                {playlistType === "row_1" && (
                  <>
                    {movies &&
                      movies.map((movie) => (
                        <Option key={movie.key} value={`movie|${movie.key}`}>
                          {movie.title}
                        </Option>
                      ))}
                    {episodes &&
                      episodes.map((ep) => (
                        <Option key={ep.key} value={`episode|${ep.key}`}>
                          {ep.title}
                        </Option>
                      ))}
                  </>
                )}
                {["row_2", "row_3", "row_4"].includes(playlistType) && (
                  <>
                    {movies &&
                      movies.map((movie) => (
                        <Option key={movie.key} value={`movie|${movie.key}`}>
                          {movie.title}
                        </Option>
                      ))}
                    {seasons &&
                      seasons.map((s) => (
                        <Option key={s._id} value={`season|${s._id}`}>
                          {s.enrichedName}
                        </Option>
                      ))}
                  </>
                )}
                {playlistType === "creators" && (
                  <>
                    {artists &&
                      artists.map((a) => (
                        <Option key={a.key} value={a.key}>
                          {a.name}
                        </Option>
                      ))}
                  </>
                )}
              </Select>
            </Form.Item>
            {content && (
              <Card title="Order of Display" style={{ marginTop: "10px" }}>
                <DndContext onDragEnd={onDragEnd}>
                  <SortableContext
                    items={content}
                    strategy={verticalListSortingStrategy}
                  >
                    {content.map((id) => (
                      <Row key={id} id={id} name={getContentName(id)} />
                    ))}
                  </SortableContext>
                </DndContext>
              </Card>
            )}
          </Form>
          <Button
            type="primary"
            onClick={handleSubmit}
            style={{
              marginTop: "10px",
              padding: "0 20px",
              float: "right",
            }}
          >
            Save
          </Button>
        </Card>
      )}
    </div>
  );
};

export default EditPlaylists;
